import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface LegalSection {
  id: string;
  titleKey: string;
  bodyKeys: string[];
}

export interface LegalLink {
  to: string;
  labelKey: string;
  icon: string;
}

interface LegalDocumentProps {
  titleKey: string;
  subtitleKey: string;
  updatedKey: string;
  sections: LegalSection[];
  documentTitle: string;
  seeAlso: LegalLink[];
}

export default function LegalDocument({
  titleKey,
  subtitleKey,
  updatedKey,
  sections,
  documentTitle,
  seeAlso,
}: LegalDocumentProps) {
  const { t } = useTranslation("common");
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    const previous = document.title;
    document.title = documentTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-25% 0px -65% 0px", threshold: 0 }
    );
    sections.forEach((section) => {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main>
      {/* Hero header */}
      <section className="relative pt-28 md:pt-36 pb-10 md:pb-14 bg-background-950 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(oklch(var(--primary-500) / 0.5) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.5) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[760px] h-[360px] bg-primary-500/6 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[520px] h-[260px] bg-accent-500/6 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-400/30 to-transparent" />

        <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-[12px] font-label text-foreground-400/50 mb-5">
              <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">
                {t("legal.breadcrumbHome")}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-500/40" />
              <span className="text-foreground-400/50">{t("legal.breadcrumbLegal")}</span>
              <i className="ri-arrow-right-s-line text-foreground-500/40" />
              <span className="text-primary-300">{t(titleKey)}</span>
            </nav>

            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/12 border border-accent-400/25 text-accent-300 text-[10px] font-label font-semibold uppercase tracking-[0.18em] mb-4">
              <i className="ri-shield-check-line" />
              {t("legal.badge")}
            </span>

            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 leading-tight mb-3">
              {t(titleKey)}
            </h1>
            <p className="text-sm md:text-base text-foreground-400/65 max-w-2xl leading-relaxed mb-4">
              {t(subtitleKey)}
            </p>
            <div className="inline-flex items-center gap-2 text-[12px] font-label text-foreground-400/50">
              <i className="ri-calendar-check-line text-primary-400/70" />
              {t(updatedKey)}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-10 md:py-14 bg-background-950">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Table of contents */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 rounded-2xl bg-background-900/60 border border-white/8 p-5">
                <h2 className="font-heading font-semibold text-[13px] text-foreground-100 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-list-check-2 text-primary-400" />
                  {t("legal.toc")}
                </h2>
                <ul className="space-y-1">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        type="button"
                        onClick={() => scrollToId(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-[12.5px] font-body leading-snug transition-all duration-200 cursor-pointer border-l-2 ${
                          activeId === section.id
                            ? "text-primary-300 border-primary-400 bg-primary-500/8"
                            : "text-foreground-400/60 border-transparent hover:text-foreground-100 hover:bg-white/4"
                        }`}
                      >
                        {t(section.titleKey)}
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => scrollToId(sections[0]?.id ?? "")}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-foreground-100/10 text-[12px] font-label font-medium text-foreground-400/60 hover:text-foreground-100 hover:border-primary-400/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-up-line" />
                  {t("legal.backToTop")}
                </button>
              </div>
            </aside>

            {/* Sections */}
            <div className="lg:col-span-8">
              <div className="rounded-2xl bg-background-900/45 border border-white/8 p-5 sm:p-8 md:p-10">
                <div className="space-y-9">
                  {sections.map((section) => (
                    <article key={section.id} id={section.id} className="scroll-mt-28">
                      <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-50 mb-3 flex items-start gap-3">
                        <span className="mt-1.5 w-1.5 h-5 rounded-full bg-gradient-to-b from-primary-400 to-accent-500 flex-shrink-0" />
                        {t(section.titleKey)}
                      </h2>
                      <div className="space-y-3 pl-[18px]">
                        {section.bodyKeys.map((bodyKey) => (
                          <p
                            key={bodyKey}
                            className="text-[13.5px] text-foreground-300/70 leading-relaxed"
                          >
                            {t(bodyKey)}
                          </p>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              {/* See also */}
              {seeAlso.length > 0 && (
                <div className="mt-6">
                  <p className="text-[11px] font-label font-semibold text-foreground-400/45 uppercase tracking-[0.18em] mb-3">
                    {t("legal.seeAlso")}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {seeAlso.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="group flex items-center gap-3 px-4 py-3.5 rounded-xl bg-background-900/60 border border-white/8 hover:border-primary-400/30 transition-all duration-300 cursor-pointer"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-400/20 text-primary-300 flex-shrink-0">
                          <i className={link.icon} />
                        </span>
                        <span className="text-[13px] font-label font-medium text-foreground-200/80 group-hover:text-primary-300 transition-colors">
                          {t(link.labelKey)}
                        </span>
                        <i className="ri-arrow-right-line ml-auto text-foreground-400/40 group-hover:text-primary-300 group-hover:translate-x-1 transition-all duration-300" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact CTA */}
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-primary-500/12 via-background-900/40 to-accent-500/10 border border-white/8 p-6 md:p-7">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-500/12 border border-primary-400/20 flex-shrink-0">
                      <i className="ri-customer-service-2-line text-lg text-primary-300" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-foreground-50 mb-1">
                        {t("legal.contact.title")}
                      </h3>
                      <p className="text-[12.5px] text-foreground-400/60 max-w-md leading-relaxed">
                        {t("legal.contact.text")}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/contactanos"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-label font-semibold text-sm hover:from-primary-400 hover:to-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer flex-shrink-0"
                  >
                    {t("legal.contact.cta")}
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}